// React.lazy wrapper for named-export route chunks that survives stale
// deploys. public/_redirects rewrites EVERY path — including a deleted
// hashed chunk — to index.html with HTTP 200, so a stale import() does
// NOT reject: it RESOLVES to a junk namespace and the picker
// `m.XPage` comes back undefined. chunkReload.ts (which listens for
// import() *rejections*) never sees it, and users get a white screen
// with "undefined is not an object (evaluating 'e.XPage')".
//
// lazyNamed() validates the namespace after the import: retry once (a
// transient network blip or a mid-deploy race often heals itself), then
// hand off to chunkReload's bounded reloadOnce(). If the reload cap is
// already spent, throw an error whose message matches
// isStaleChunkError's regexes so ErrorBoundary + Sentry paths behave
// exactly as a rejected chunk import does today.

import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { reloadOnce } from "@/lib/chunkReload";

const RETRY_DELAY_MS = 250;

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyModule = Record<string, any>;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function attempt(
  importer: () => Promise<AnyModule>,
): Promise<AnyModule | undefined> {
  try {
    return await importer();
  } catch {
    // A genuine rejection (offline, blocked request) gets the same
    // retry-then-reload treatment as a poisoned 200 resolution.
    return undefined;
  }
}

export function lazyNamed(
  importer: () => Promise<AnyModule>,
  exportName: string,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(async () => {
    let mod = await attempt(importer);
    if (!mod || mod[exportName] === undefined) {
      await delay(RETRY_DELAY_MS);
      mod = await attempt(importer);
    }
    if (!mod || mod[exportName] === undefined) {
      reloadOnce();
      // Reached only when the reload was suppressed (cap spent /
      // dev-mode skip): reload() never returns control to this tab
      // otherwise. Message deliberately matches isStaleChunkError.
      throw new Error(
        `Failed to fetch dynamically imported module: stale chunk (${exportName})`,
      );
    }
    return { default: mod[exportName] as ComponentType<any> };
  });
}
/* eslint-enable @typescript-eslint/no-explicit-any */
